import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import TileCTA from './TileCTA';

interface TileProps {
  tile: TileConfig;
}

export default function Tile({ tile }: TileProps) {
  const isMuted = tile.status === 'coming-soon' || tile.status === 'private';

  return (
    <article
      style={{
        background: 'var(--bg-surface)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: isMuted ? 0.78 : 1,
        transition: `transform var(--dur-base) var(--ease-out),
                     box-shadow var(--dur-base) var(--ease-out),
                     border-color var(--dur-base) var(--ease-out)`,
        cursor: isMuted ? 'default' : 'pointer',
      }}
      onMouseEnter={(e) => {
        if (isMuted) return;
        const el = e.currentTarget as HTMLElement;
        el.style.transform = 'translateY(-2px)';
        el.style.boxShadow = 'var(--shadow-2)';
        el.style.borderColor = 'var(--dp-stone-400)';
      }}
      onMouseLeave={(e) => {
        if (isMuted) return;
        const el = e.currentTarget as HTMLElement;
        el.style.transform = '';
        el.style.boxShadow = '';
        el.style.borderColor = 'var(--border-default)';
      }}
    >
      {/* Header strip */}
      <div
        style={{
          height: '120px',
          background: 'linear-gradient(180deg, var(--dp-canvas-200) 0%, var(--dp-canvas-100) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Status badge top-right */}
        {tile.status !== 'public' && (
          <span
            style={{
              position: 'absolute',
              top: 'var(--sp-3)',
              right: 'var(--sp-3)',
              fontSize: 'var(--fs-eyebrow)',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 'var(--radius-sm)',
              background: tile.status === 'coming-soon' ? 'var(--dp-warn-100)' : 'var(--dp-red-100)',
              color: tile.status === 'coming-soon' ? 'var(--dp-warn-700)' : 'var(--dp-red-700)',
            }}
          >
            {tile.status === 'coming-soon' ? t('tile.badge.soon') : t('tile.badge.private')}
          </span>
        )}
        {/* Navy mark */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--dp-navy-800)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 600,
              fontSize: '24px',
              color: 'var(--fg-on-navy)',
              lineHeight: 1,
            }}
          >
            {tile.mark}
          </span>
          <span
            className="dp-red-dot"
            style={{
              position: 'absolute',
              bottom: '6px',
              right: '6px',
              width: '6px',
              height: '6px',
              margin: 0,
            }}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', flex: 1 }}>
        <h3
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--fs-h3)',
            fontWeight: 600,
            color: 'var(--fg-1)',
            lineHeight: 'var(--lh-h3)',
          }}
        >
          {tile.title}
        </h3>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--fg-3)',
            lineHeight: 'var(--lh-body-sm)',
            margin: 0,
          }}
        >
          {tile.description}
        </p>
        {/* Tags */}
        {tile.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
            {tile.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-eyebrow)',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--dp-navy-050)',
                  color: 'var(--fg-2)',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {/* CTA separator + button */}
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 'var(--sp-3)',
            borderTop: '1px solid var(--border-subtle)',
          }}
        >
          <TileCTA tile={tile} />
        </div>
      </div>
    </article>
  );
}
